/**
 * LEVOR Remote Desktop Web Client - Type Definitions
 */

// ============================================================================
// Connection Types
// ============================================================================

export interface ServerConfig {
    url: string;
    hubPath: string;
    reconnectIntervals: number[];
}

export interface ConnectionState {
    isConnected: boolean;
    isStreaming: boolean;
    isP2PConnected: boolean;
    isEncrypted: boolean;
    connectionId: string | null;
    sessionId: string | null;
}

export enum ConnectionStatus {
    Disconnected = 'disconnected',
    Connecting = 'connecting',
    Connected = 'connected',
    Streaming = 'streaming',
    Reconnecting = 'reconnecting',
    Error = 'error'
}

// ============================================================================
// Agent Types
// ============================================================================

export interface AgentInfo {
    // Fields from C# AgentInfo model (camelCase serialization)
    agentId: string;           // Used as connection identifier
    machineName: string;
    username: string;          // Note: lowercase 'n'
    screenWidth: number;
    screenHeight: number;
    operatingSystem: string;
    connectedAt: Date;
    isAvailable: boolean;      // Agent availability status
    monitors: MonitorInfo[];
    selectedMonitorIndex: number;
    persistentAgentId?: string;
    requiresApproval?: boolean;
}

export interface MonitorInfo {
    index: number;
    deviceName: string;
    friendlyName: string;
    isPrimary: boolean;
    left: number;
    top: number;
    width: number;
    height: number;
    refreshRate: number;
}

// ============================================================================
// Session Types
// ============================================================================

export interface SessionInfo {
    sessionId: string;
    agentId: string;
    clientId: string;
    startTime: Date;
    isActive: boolean;
    isEncrypted: boolean;
    selectedMonitor: number;
}

export interface SessionRequest {
    agentId: string;
    requestedMonitor?: number;
    enableEncryption?: boolean;
}

// ============================================================================
// Video Types
// ============================================================================

export enum VideoCodec {
    Jpeg = 0,
    H264 = 1,
    Vp8 = 2,
    Vp9 = 3,
    Av1 = 4
}

export interface FrameData {
    data: Uint8Array;
    width: number;
    height: number;
    codec: VideoCodec;
    isKeyFrame: boolean;
    frameNumber: number;
    timestamp: number;
}

export interface VideoStats {
    fps: number;
    bitrate: number;
    frameCount: number;
    droppedFrames: number;
    decodeTimeMs: number;
    renderTimeMs: number;
    latencyMs: number;
}

export interface DecoderConfig {
    codec: VideoCodec;
    width: number;
    height: number;
    hardwareAcceleration?: 'prefer-hardware' | 'prefer-software' | 'no-preference';
}

// ============================================================================
// Input Types
// ============================================================================

export enum InputType {
    MouseMove = 0,
    MouseDown = 1,
    MouseUp = 2,
    MouseWheel = 3,
    KeyDown = 4,
    KeyUp = 5,
    TextInput = 6
}

export enum MouseButton {
    Left = 0,
    Middle = 1,
    Right = 2,
    X1 = 3,
    X2 = 4
}

export interface InputCommand {
    type: InputType;
    x?: number;
    y?: number;
    button?: MouseButton;
    wheelDelta?: number;
    keyCode?: number;
    scanCode?: number;
    text?: string;
    modifiers?: KeyModifiers;
    timestamp: number;
}

export interface KeyModifiers {
    ctrl: boolean;
    alt: boolean;
    shift: boolean;
    meta: boolean;
}

// ============================================================================
// WebRTC Types
// ============================================================================

// Must match C# WebRTCSignalType enum in WebRTCModels.cs (integer values)
export enum WebRTCSignalType {
    Offer = 0,
    Answer = 1,
    IceCandidate = 2,
    IceGatheringComplete = 3,
    ConnectionStateChange = 4,
    DataChannelOpen = 5,
    DataChannelClose = 6
}

export interface WebRTCSignal {
    type: WebRTCSignalType;
    sessionId: string;
    sdp?: string;
    sdpType?: string;
    iceCandidate?: IceCandidateInfo;
}

export interface IceCandidateInfo {
    candidate: string;
    sdpMid: string | null;
    sdpMLineIndex: number | null;
    usernameFragment?: string;
}

export interface WebRTCConfig {
    stunServers: string[];
    turnServer?: TurnServerConfig;
    dataChannelBufferThreshold: number;
    connectionTimeoutMs: number;
}

export interface TurnServerConfig {
    url: string;
    username: string;
    credential: string;
}

export enum TurnProvider {
    None = 'None',
    Twilio = 'Twilio',
    Coturn = 'Coturn'
}

export interface IceServerInfo {
    urls: string[];
    username?: string;
    credential?: string;
}

export interface IceServersResponse {
    iceServers: IceServerInfo[];
    ttlSeconds: number;
    provider: TurnProvider;
}

export interface WebRTCStats {
    bytesSent: number;
    bytesReceived: number;
    packetsSent: number;
    packetsReceived: number;
    roundTripTimeMs: number;
    lastUpdated: Date;
}

// ============================================================================
// Encryption Types
// ============================================================================

export enum EncryptionState {
    None = 0,
    Negotiating = 1,
    Established = 2,
    Failed = 3
}

export interface KeyExchangeRequest {
    publicKey: string;
    nonce: string;
    timestamp: number;
    version: number;
}

export interface KeyExchangeResponse {
    publicKey: string;
    requestNonce: string;
    responseNonce: string;
    timestamp: number;
    accepted: boolean;
    errorMessage?: string;
}

export interface EncryptedMessage {
    type: string;
    payload: string;
    sequenceNumber: number;
    timestamp: number;
}

export interface EncryptedFrame {
    data: string;
    frameNumber: number;
    width: number;
    height: number;
    codec: number;
    isKeyFrame: boolean;
}

// ============================================================================
// Authentication Types
// ============================================================================

export interface AuthCredentials {
    username: string;
    password: string;
}

export interface AuthToken {
    accessToken: string;
    refreshToken: string;
    expiresAt: Date;
    tokenType: string;
}

export interface UserInfo {
    id: string;
    username: string;
    email: string;
    role: string;
    permissions: string[];
}

// ============================================================================
// UI Types
// ============================================================================

export interface ViewerSettings {
    quality: QualityPreset;
    fitToWindow: boolean;
    showToolbar: boolean;
    showStats: boolean;
    captureKeyboard: boolean;
    enableClipboard: boolean;
    enableAudio: boolean;
}

export enum QualityPreset {
    Auto = 'auto',
    Low = 'low',
    Medium = 'medium',
    High = 'high',
    Ultra = 'ultra'
}

export interface ToastMessage {
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    duration?: number;
}

// ============================================================================
// Event Types
// ============================================================================

export type ConnectionEventHandler = (state: ConnectionState) => void;
export type FrameEventHandler = (frame: FrameData) => void;
export type StatsEventHandler = (stats: VideoStats) => void;
export type ErrorEventHandler = (error: Error) => void;
export type AgentListEventHandler = (agents: AgentInfo[]) => void;

// ============================================================================
// DataChannel Protocol Types
// ============================================================================

// Must match C# DataChannelMessageType in WebRTCModels.cs
export enum DataChannelMessageType {
    Frame = 1,
    Input = 2,
    Audio = 3,
    FileChunk = 4,
    Clipboard = 5,
    Ping = 6,
    Pong = 7,
    Stats = 8
}

export interface DataChannelMessage {
    type: DataChannelMessageType;
    payload: Uint8Array;
}
